/* -----------------------------------
   WAIT FOR DOM + GOOGLE MAPS
   (prevents "google is not defined")
----------------------------------- */

function waitForGooglePlaces(timeoutMs = 15000) {
  return new Promise((resolve, reject) => {
    const start = Date.now();

    (function check() {
      if (
        window.google &&
        window.google.maps &&
        typeof window.google.maps.importLibrary === "function"
      ) {
        resolve();
        return;
      }

      if (Date.now() - start > timeoutMs) {
        reject(new Error("Google Maps script did not load. Check the script tag, API key and console for Google errors."));
        return;
      }

      setTimeout(check, 100);
    })();
  });
}

function onDomReady(fn) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", fn);
  } else {
    fn();
  }
}


onDomReady(async function () {
  const addressInput = document.getElementById("address_line_1");

  if (!addressInput) {
    console.error("Address input not found.");
    return;
  }

  try {
    // Wait until the Google script is actually ready
    await waitForGooglePlaces();

    // Load Google Places API
    const {
      AutocompleteSuggestion,
      AutocompleteSessionToken
    } = await google.maps.importLibrary("places");

    let suggestionsContainer = null;
    let sessionToken = new AutocompleteSessionToken();

    // Used to prevent old API responses from reopening dropdown
    let requestId = 0;

    // Prevent dropdown from reopening after selection
    let isAddressSelected = false;


    /* -----------------------------------
       CREATE SUGGESTION CONTAINER
    ----------------------------------- */

    function createSuggestionsContainer() {
      if (suggestionsContainer) {
        return;
      }

      const parent = addressInput.parentElement;
      parent.style.position = "relative";

      suggestionsContainer = document.createElement("div");
      suggestionsContainer.className = "google-address-suggestions";
      suggestionsContainer.style.display = "none";

      parent.appendChild(suggestionsContainer);
    }

    createSuggestionsContainer();


    /* -----------------------------------
       CLEAR SUGGESTIONS
    ----------------------------------- */

    function clearSuggestions() {
      if (!suggestionsContainer) {
        return;
      }

      suggestionsContainer.innerHTML = "";
      suggestionsContainer.style.display = "none";
    }


    /* -----------------------------------
       DISPLAY SUGGESTIONS
    ----------------------------------- */

    function displaySuggestions(suggestions) {
      // Do not reopen dropdown after an address has been selected.
      if (isAddressSelected) {
        return;
      }

      suggestionsContainer.innerHTML = "";

      if (!suggestions || suggestions.length === 0) {
        suggestionsContainer.style.display = "none";
        return;
      }

      suggestions.forEach((suggestion) => {
        const placePrediction = suggestion.placePrediction;

        if (!placePrediction) {
          return;
        }

        const item = document.createElement("div");
        item.className = "google-address-suggestion";
        item.textContent = placePrediction.text.toString();

        // Select address BEFORE the input can lose focus.
        item.addEventListener("mousedown", function (event) {
          event.preventDefault();
          event.stopPropagation();

          isAddressSelected = true; // Lock autocomplete immediately.
          requestId++;              // Invalidate pending requests.
          clearSuggestions();       // Close dropdown immediately.

          selectPlace(placePrediction);
        });

        suggestionsContainer.appendChild(item);
      });

      // Final safety check.
      if (!isAddressSelected) {
        suggestionsContainer.style.display = "block";
      }
    }


    /* -----------------------------------
       ADDRESS INPUT
    ----------------------------------- */

    addressInput.addEventListener("input", async function (event) {
      // Ignore synthetic events dispatched from selectPlace().
      // Only proceed if the event was triggered by actual user typing.
      if (!event.isTrusted) {
        return;
      }

      // User is typing again, so autocomplete can work again.
      isAddressSelected = false;

      const value = addressInput.value.trim();

      if (value.length < 3) {
        requestId++;
        clearSuggestions();
        return;
      }

      // Create unique request ID.
      const currentRequestId = ++requestId;

      try {
        const request = {
          input: value,
          includedRegionCodes: ["us"],   // Restricts results to US only
          region: "us",                  // US formatting
          includedPrimaryTypes: ["street_address", "premise", "subpremise", "route"],
          sessionToken: sessionToken
        };

        const { suggestions } =
          await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        // If the user selected an address while this was in flight,
        // or if a newer request was made, bail out.
        if (currentRequestId !== requestId || isAddressSelected) {
          return;
        }

        displaySuggestions(suggestions);

      } catch (error) {
        console.error("Google autocomplete error:", error);
        clearSuggestions();
      }
    });


    /* -----------------------------------
       SET STATE (text input or dropdown)
    ----------------------------------- */

    function setStateValue(stateInput, stateLong, stateShort) {
      if (!stateInput) {
        return;
      }

      if (stateInput.tagName === "SELECT") {
        // Try full name first ("California"), then code ("CA")
        const options = Array.from(stateInput.options);
        const match =
          options.find((o) => o.value === stateLong || o.text.trim() === stateLong) ||
          options.find((o) => o.value === stateShort || o.text.trim() === stateShort);

        stateInput.value = match ? match.value : "";
      } else {
        stateInput.value = stateLong;
      }
    }


    /* -----------------------------------
       SELECT ADDRESS
    ----------------------------------- */

    async function selectPlace(placePrediction) {
      isAddressSelected = true; // Keep dropdown locked.
      requestId++;              // Invalidate pending requests.
      clearSuggestions();       // Close dropdown.

      try {
        const place = placePrediction.toPlace();

        // Request only required fields.
        await place.fetchFields({
          fields: ["addressComponents", "formattedAddress"]
        });

        const components = place.addressComponents || [];

        let streetNumber = "";
        let route = "";
        let addressLine2 = "";
        let city = "";
        let sublocality = "";
        let state = "";
        let stateShort = "";
        let zipcode = "";

        components.forEach((component) => {
          const types = component.types || [];
          const value = component.longText || "";

          // Street number
          if (types.includes("street_number")) {
            streetNumber = value;
          }

          // Street name
          if (types.includes("route")) {
            route = value;
          }

          // Apartment / Unit
          if (types.includes("subpremise")) {
            addressLine2 = value;
          }

          // City
          if (types.includes("locality") || types.includes("postal_town")) {
            city = value;
          }

          // Fallback city (e.g. Brooklyn, Queens have no "locality")
          if (types.includes("sublocality_level_1") || types.includes("sublocality")) {
            sublocality = value;
          }

          // State
          if (types.includes("administrative_area_level_1")) {
            state = value;
            stateShort = component.shortText || "";
          }

          // ZIP
          if (types.includes("postal_code")) {
            zipcode = value;
          }
        });

        // Build line 1 regardless of the order Google returns components
        let addressLine1 = [streetNumber, route].filter(Boolean).join(" ");

        if (!addressLine1 && placePrediction.mainText) {
          addressLine1 = placePrediction.mainText.toString();
        }

        if (!city) {
          city = sublocality;
        }


        /* -----------------------------------
           FILL YOUR EXISTING FIELDS
        ----------------------------------- */

        const addressLine1Input = document.getElementById("address_line_1");
        const addressLine2Input = document.getElementById("address_line_2");
        const cityInput = document.getElementById("city");
        const stateInput = document.getElementById("state");
        const zipInput = document.getElementById("zip");

        if (addressLine1Input) {
          addressLine1Input.value = addressLine1;
        }

        if (addressLine2Input) {
          addressLine2Input.value = addressLine2;
        }

        if (cityInput) {
          cityInput.value = city;
        }

        setStateValue(stateInput, state, stateShort);

        if (zipInput) {
          zipInput.value = zipcode;
        }


        /* -----------------------------------
           TRIGGER EXISTING FORM EVENTS
        ----------------------------------- */

        [addressLine1Input, addressLine2Input, cityInput, zipInput].forEach((element) => {
          if (!element) {
            return;
          }

          element.dispatchEvent(new Event("input", { bubbles: true }));
          element.dispatchEvent(new Event("change", { bubbles: true }));
        });

        if (stateInput) {
          stateInput.dispatchEvent(new Event("change", { bubbles: true }));
        }

        // Start a new Google session.
        sessionToken = new AutocompleteSessionToken();

        // Final cleanup.
        clearSuggestions();

      } catch (error) {
        console.error("Error getting selected address:", error);
        clearSuggestions();
      }
    }


    /* -----------------------------------
       CLOSE WHEN CLICKING OUTSIDE
    ----------------------------------- */

    document.addEventListener("click", function (event) {
      if (
        !addressInput.contains(event.target) &&
        !suggestionsContainer.contains(event.target)
      ) {
        clearSuggestions();
      }
    });

    console.log("Google address autocomplete initialized successfully.");

  } catch (error) {
    console.error("Failed to initialize Google Places:", error);
  }
});
// address autocomplete function ends



document.addEventListener("DOMContentLoaded", function () {
  const form = document.getElementById("customerInfoForm");
  if (!form) return;

  const submitBtn = form.querySelector(".user-details-submit-btn");

  //name field sanitization
    const nameFields = [
      document.getElementById('first_name'),
      document.getElementById('last_name')
    ];

    nameFields.forEach(function (field) {
      if (!field) return;

    field.addEventListener('input', function () {
        // Allow only letters, spaces, and hyphens
        this.value = this.value.replace(/[^a-zA-Z -]/g, '');
      });
    });
  //end

  const formatDOB = (input) => {
    const digits = input.value.replace(/\D/g, "").slice(0, 8);
    let formatted = digits;
    if (digits.length >= 4) {
      formatted =
        digits.slice(0, 2) + "/" + digits.slice(2, 4) + "/" + digits.slice(4);
    } else if (digits.length >= 2) {
      formatted = digits.slice(0, 2) + "/" + digits.slice(2);
    }
    input.value = formatted;
  };

  const isValidDOB = (val) => {
    if (!val || val.length !== 10) return false;
    const parts = val.split("/");
    if (parts.length !== 3) return false;
    const month = parseInt(parts[0], 10);
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    if (isNaN(month) || isNaN(day) || isNaN(year)) return false;
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    if (year < 1000 || year > 9999) return false;
    const date = new Date(year, month - 1, day);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    )
      return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const hundredYearsAgo = new Date();
    hundredYearsAgo.setFullYear(today.getFullYear() - 100);
    return date <= today && date >= hundredYearsAgo;
  };

  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const fieldDefs = [
    { key: "first_name", id: "first_name", attrName: "First Name" },
    { key: "last_name", id: "last_name", attrName: "Last Name" },
    {
      key: "dob",
      id: "dob",
      attrName: "DOB",
      validate: isValidDOB,
      invalidMessage: "Please enter a valid date (MM/DD/YYYY)",
    },
    { key: "gender", id: "gender", attrName: "Gender" },
    {
      key: "email",
      id: "email",
      attrName: "Email",
      validate: isValidEmail,
      invalidMessage: "Please enter a valid email address",
    },
    { key: "phone", id: "phone", attrName: "Phone" },
    { key: "address_line_1", id: "address_line_1", attrName: "Address Line 1" },
    { key: "address_line_2", id: "address_line_2", attrName: "Address Line 2" },
    { key: "city", id: "city", attrName: "City" },
    { key: "state", id: "state", attrName: "State" },
    { key: "zip", id: "zip", attrName: "Zipcode" },
  ];

  const fields = {};

  fieldDefs.forEach((def) => {
    const input = document.getElementById(def.id);
    if (!input) return;
    fields[def.key] = {
      input,
      error: document.getElementById(def.id + "_error"),
      attrName: def.attrName,
      required: input.dataset.required !== "false",
      validate: def.validate,
      invalidMessage: def.invalidMessage,
    };
  });

  const termsCheckbox = document.getElementById("terms_checkbox");
  if (termsCheckbox) {
    fields.terms = {
      input: termsCheckbox,
      error: document.getElementById("terms_error"),
      attrName: "Terms Accepted",
      required: true,
      isCheckbox: true,
    };
  }

  const validateField = (key) => {
    const field = fields[key];
    if (!field) return true;
    let valid = true;
    let msg = "";

    if (field.isCheckbox) {
      if (field.required && !field.input.checked) {
        valid = false;
        msg = "You must accept the Terms of Service";
      }
    } else {
      const value = field.input.value.trim();
      if (field.required && !value) {
        valid = false;
        msg = "This field is required";
      } else if (
        value &&
        (key === "phone" || field.attrName === "phone") &&
        value.replace(/\D/g, "").length < 10
      ) {
        valid = false;
        msg = "Please enter 10 digit phone number";
      } else if (value && field.validate && !field.validate(value)) {
        valid = false;
        msg = field.invalidMessage || "Please enter a valid value";
      }
    }

    field.error.textContent = msg;
    return valid;
  };

  const validateForm = () => {
    const allValid = Object.keys(fields).every((key) => {
      const field = fields[key];
      if (field.isCheckbox) {
        return !field.required || field.input.checked;
      }
      const value = field.input.value.trim();
      if (field.required && !value) return false;

      // Phone number validation
      if (
        value &&
        (key === "phone" || field.attrName === "phone") &&
        value.replace(/\D/g, "").length < 10
      ) {
        return false;
      }
      if (value && field.validate && !field.validate(value)) return false;
      return true;
    });

    submitBtn.disabled = !allValid;
  };

  if (fields.dob) {
    const dobInput = fields.dob.input;

    dobInput.addEventListener("keydown", (e) => {
      const allowed = ["Delete", "ArrowLeft", "ArrowRight", "Tab"];
      if (allowed.includes(e.key) || e.metaKey || e.ctrlKey) return;

      if (e.key === "Backspace") {
        const pos = dobInput.selectionStart;
        if (dobInput.value[pos - 1] === "/") {
          e.preventDefault();
          dobInput.value =
            dobInput.value.slice(0, pos - 1) + dobInput.value.slice(pos);
          dobInput.setSelectionRange(pos - 1, pos - 1);
          validateForm();
        }
        return;
      }

      if (!/^\d$/.test(e.key)) e.preventDefault();
    });

    dobInput.addEventListener("paste", (e) => {
      e.preventDefault();
      const text = (e.clipboardData || window.clipboardData).getData("text");
      dobInput.value = text;
      formatDOB(dobInput);
      validateForm();
    });
  }

  if (fields.phone) {
    const phoneInput = fields.phone.input;

    phoneInput.addEventListener("keydown", (e) => {
      const allowed = ["Delete", "Backspace", "ArrowLeft", "ArrowRight", "Tab"];
      if (allowed.includes(e.key) || e.metaKey || e.ctrlKey) return;
      if (!/^\d$/.test(e.key)) e.preventDefault();
    });

    phoneInput.addEventListener("paste", (e) => {
      e.preventDefault();
      const text = (e.clipboardData || window.clipboardData).getData("text");
      phoneInput.value = text.replace(/\D/g, "");
      validateForm();
    });
  }

  Object.keys(fields).forEach((key) => {
    const field = fields[key];

    if (field.isCheckbox) {
      field.input.addEventListener("change", () => {
        field.error.textContent = "";
        validateForm();
      });
      field.input.addEventListener("blur", () => validateField(key));
      return;
    }

    field.input.addEventListener("input", () => {
      if (key === "dob") formatDOB(field.input);
      field.error.textContent = "";
      validateForm();
    });

    field.input.addEventListener("blur", () => validateField(key));
  });

  form.addEventListener("submit", async function (e) {
    e.preventDefault();

    let isFormValid = true;
    for (let key in fields) {
      if (!validateField(key)) isFormValid = false;
    }
    if (!isFormValid) return;

    const attributes = {};
Object.keys(fields).forEach((key) => {
  const field = fields[key];
  if (field.isCheckbox) {
    attributes[key] = field.input.checked ? "Yes" : "No";
  } else {
    // Send the trimmed value directly, even if it is an empty string ""
    attributes[key] = field.input.value.trim();
  }
});

submitBtn.disabled = true;
submitBtn.textContent = "Saving...";

try {
  // 1. Update cart note_attributes (passing "" clears out previous values)
  await fetch("/cart/update.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ attributes }),
  });

  // 2. Fetch updated cart items
  const cart = await fetch("/cart.js").then((r) => r.json());

  // 3. Update line item properties
  const updates = {};
  cart.items.forEach((item) => {
    updates[item.key] = {
      quantity: item.quantity,
      properties: { ...attributes },
    };
  });

  await fetch("/cart/update.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ updates }),
  });

      // console.log("FINAL CART:", JSON.stringify({cart}));
      //console.log("Final Attributes:", JSON.stringify({attributes}));
      //console.log("Final Name:", attributes.first_name);

      
      const addressLine2 = attributes.address_line_2 || "";
      const checkoutUrl =
      "/cart/checkout" +
      "?checkout[email]=" + encodeURIComponent(attributes.email) +
      "&checkout[shipping_address][first_name]=" + encodeURIComponent(attributes.first_name) +
      "&checkout[shipping_address][last_name]=" + encodeURIComponent(attributes.last_name) +
      "&checkout[shipping_address][address1]=" + encodeURIComponent(attributes.address_line_1) +
      "&checkout[shipping_address][address2]=" + encodeURIComponent(addressLine2) +
      "&checkout[shipping_address][city]=" + encodeURIComponent(attributes.city) +
      "&checkout[shipping_address][province]=" + encodeURIComponent(attributes.state) +
      "&checkout[shipping_address][zip]=" + encodeURIComponent(attributes.zip) +
      "&checkout[billing_address][first_name]=" + encodeURIComponent(attributes.first_name) +
      "&checkout[billing_address][last_name]=" + encodeURIComponent(attributes.last_name) +
      "&checkout[billing_address][address1]=" + encodeURIComponent(attributes.address_line_1) +
      "&checkout[billing_address][address2]=" + encodeURIComponent(addressLine2) +
      "&checkout[billing_address][city]=" + encodeURIComponent(attributes.city) +
      "&checkout[billing_address][province]=" + encodeURIComponent(attributes.state) +
      "&checkout[billing_address][zip]=" + encodeURIComponent(attributes.zip);

      //console.log(checkoutUrl);
      window.location.href = checkoutUrl;
    } catch (err) {
      console.error(err);
      alert("Something went wrong.");
      submitBtn.disabled = false;
      submitBtn.textContent = submitBtn.dataset.defaultLabel;
    }
  });

  validateForm();
});
